import { isTurnActive, toChatError, unwrapConversationRecords, extractToolUseBlocks, isAwaitingApproval, findLastAssistantMessage, isHumanUserMessage, collectResolvedToolUseIds, getToolUseIdForResult } from '../utils/chatHelpers';
function extractFeedbackByTurnId(feedbackByKey) {
  const map = new Map();
  for (const [key, entry] of Object.entries(feedbackByKey !== null && feedbackByKey !== void 0 ? feedbackByKey : {})) {
    if (key.includes(':')) continue; // skip citation-level keys
    const turnId = parseInt(key, 10);
    if (!isNaN(turnId) && (entry.feedbackType === 'THUMBS_UP' || entry.feedbackType === 'THUMBS_DOWN')) {
      map.set(turnId, entry.feedbackType);
    }
  }
  return map;
}
function extractFeedbackByCitationKey(feedbackByKey) {
  const map = new Map();
  for (const [key, entry] of Object.entries(feedbackByKey !== null && feedbackByKey !== void 0 ? feedbackByKey : {})) {
    if (!key.includes(':')) continue; // only citation-level keys ({turnId}:{citationId})
    if (entry.feedbackType === 'THUMBS_UP' || entry.feedbackType === 'THUMBS_DOWN') {
      map.set(key, entry.feedbackType);
    }
  }
  return map;
}
export function createConversationLoader(ctx, conversationHistory, callbacks) {
  async function loadConversation(id) {
    ctx.logger.conversationLoad(id);
    ctx.transport.stop();
    ctx.transport.clear();
    ctx.resetPendingSend();
    ctx.dispatch({
      type: 'RESET'
    });
    ctx.dispatch({
      type: 'TURN_STARTED',
      conversationId: id,
      turnId: 0
    });
    ctx.dispatch({
      type: 'STATUS_SET',
      status: 'LOADING'
    });
    ctx.dispatch({
      type: 'HISTORY_INCREMENT_FETCH_ID'
    });
    const currentFetchId = ctx.getState().fetchIdCounter;
    try {
      const response = await conversationHistory.getConversation({
        conversationId: id,
        sortDirection: 'DESC'
      });
      if (ctx.getState().fetchIdCounter !== currentFetchId) return;
      const records = response.results;
      const lastRecord = records.length > 0 ? records[0] : undefined;
      const unwrapped = unwrapConversationRecords(records);
      ctx.dispatch({
        type: 'HISTORY_LOADED',
        messages: unwrapped.messages,
        hasMore: response.hasMore,
        offset: response.offset,
        agentId: response.agentId,
        turnIdMap: unwrapped.turnIdMap,
        feedbackByTurnId: extractFeedbackByTurnId(response.feedbackByKey),
        feedbackByCitationKey: extractFeedbackByCitationKey(response.feedbackByKey)
      });
      if (response.title) {
        ctx.dispatch({
          type: 'TITLE_UPDATED',
          title: response.title
        });
      }
      ctx.logger.conversationLoaded(id, records.length, response.hasMore);

      // A terminal conversation status means the turn already ended — don't
      // attempt to resume even when the last persisted record is a DISPLAY_ONLY
      // progress marker with no stopReason (which would otherwise fool
      // isTurnActive into returning true). FAILED is handled separately below
      // to surface an error rather than silently going to READY.
      const isConversationFailed = response.status === 'FAILED';
      const isConversationTerminal = response.status === 'COMPLETED' || response.status === 'DELETED' || isConversationFailed;
      const loadedMessages = ctx.getState().messages;
      if (!isConversationTerminal && isTurnActive(loadedMessages) && lastRecord) {
        var _callbacks$isClientTo, _toolUseExtracted$too, _toolUseExtracted$too2;
        const lastTurnId = lastRecord.turnId;
        const lastMessageRecordId = lastRecord.id;
        ctx.dispatch({
          type: 'TURN_STARTED',
          conversationId: id,
          turnId: lastTurnId
        });
        const toolUseExtracted = extractToolUseBlocks(loadedMessages);
        const lastAssistantMessage = findLastAssistantMessage(loadedMessages);
        const lastReason = lastAssistantMessage === null || lastAssistantMessage === void 0 ? void 0 : lastAssistantMessage.stopReason;

        // A genuine human message after the tool call means the user already
        // answered, so the turn is no longer parked on the client. Anchor on the
        // last ASSISTANT message because the pause can be trailed by a
        // backend-persisted PENDING_TOOL_RESULT placeholder (a USER record with
        // no human-authored content).
        const lastAssistantIndex = lastAssistantMessage ? loadedMessages.lastIndexOf(lastAssistantMessage) : -1;
        const hasHumanFollowUp = lastAssistantIndex >= 0 && loadedMessages.slice(lastAssistantIndex + 1).some(isHumanUserMessage);

        // A client-tool pause persists stopReason TOOL_USE (EXTERNAL_TOOL is
        // only a client-side runtime remap), so detect it the way
        // handleExternalTool does: an unresolved tool_use block for a tool THIS
        // client executes. Server-side tool_use is left to transport.resume().
        // extractToolUseBlocks already scopes to the last ASSISTANT message, so
        // these blocks belong to the current turn, not an older one.
        const isClientTool = (_callbacks$isClientTo = callbacks.isClientTool) !== null && _callbacks$isClientTo !== void 0 ? _callbacks$isClientTo : () => false;
        const resolvedToolUseIds = collectResolvedToolUseIds(loadedMessages);
        const isUnresolved = block => !resolvedToolUseIds.has(getToolUseIdForResult(block));
        const pendingClientToolBlocks = ((_toolUseExtracted$too = toolUseExtracted === null || toolUseExtracted === void 0 ? void 0 : toolUseExtracted.toolUseBlocks) !== null && _toolUseExtracted$too !== void 0 ? _toolUseExtracted$too : []).filter(block => isClientTool(block.name) && isUnresolved(block));
        // Approval-required tools route through the approval restore path,
        // reported via the TOOLS_REQUIRE_APPROVAL stop reason. Gate on the
        // approval still being unresolved so a granted-but-still-flagged turn
        // resumes instead of re-prompting a stale approval.
        const isApprovalPause = isAwaitingApproval(lastReason) && ((_toolUseExtracted$too2 = toolUseExtracted === null || toolUseExtracted === void 0 ? void 0 : toolUseExtracted.toolUseBlocks.some(isUnresolved)) !== null && _toolUseExtracted$too2 !== void 0 ? _toolUseExtracted$too2 : false);
        const isPausedOnTool = !hasHumanFollowUp && (isApprovalPause || pendingClientToolBlocks.length > 0);
        if (isPausedOnTool) {
          // The turn is paused waiting for the client. Resuming the stream
          // would yield no events (the backend can't proceed until it receives
          // the tool result/approval), causing processStream to call
          // onComplete() and clear activeTools. Skip the resume and restore the
          // pending state directly so the turn renders correctly after reload.
          // Approval-required tools (e.g. ExitPlanMode) need the approval
          // restore path — the external-tool path filters to client-resolved
          // tools only and would drop them, leaving the turn stuck in LOADING.
          ctx.telemetry.clientPauseRestored({
            source: 'reload',
            kind: isApprovalPause ? 'approval' : 'external-tool'
          });
          if (isApprovalPause) {
            var _callbacks$onPendingA;
            await ((_callbacks$onPendingA = callbacks.onPendingApprovalFound) === null || _callbacks$onPendingA === void 0 ? void 0 : _callbacks$onPendingA.call(callbacks));
          } else {
            var _callbacks$onPendingT;
            await ((_callbacks$onPendingT = callbacks.onPendingToolsFound) === null || _callbacks$onPendingT === void 0 ? void 0 : _callbacks$onPendingT.call(callbacks));
          }
        } else {
          ctx.dispatch({
            type: 'STATUS_SET',
            status: ctx.transport.activeStatus
          });
          await ctx.transport.resume({
            conversationId: id,
            turnId: lastTurnId,
            lastMessageId: lastMessageRecordId
          }, Object.assign({}, ctx.createHandler(), {
            onError(err) {
              ctx.logger.transportError(err);
              if (err.type === 'NOT_FOUND' || err.type === 'UNKNOWN' || err.type === 'INVALID_REQUEST') {
                // Turn is no longer available or already completed (e.g.
                // completed before reconnect, or stale turn state after
                // page reload). This is an expected outcome on reload, not a
                // failure — do not count it as a stream-error. History is
                // already loaded; go to READY so the conversation remains
                // usable rather than showing a permanent error state.
                ctx.dispatch({
                  type: 'STATUS_SET',
                  status: 'READY'
                });
              } else {
                // A real resume failure: this is the single place the resume
                // error is surfaced, so count it here (the transport defers
                // resume-error counting to the initiator).
                ctx.telemetry.streamError(err);
                ctx.dispatch({
                  type: 'TURN_ERROR',
                  error: err
                });
              }
            }
          }));
        }
      } else if (isConversationFailed) {
        ctx.dispatch({
          type: 'TURN_ERROR',
          error: toChatError(new Error('Conversation failed'))
        });
      } else {
        var _ctx$transport$precon, _ctx$transport;
        ctx.dispatch({
          type: 'STATUS_SET',
          status: 'READY'
        });
        // Preconnect to the Ably channel while the conversation is idle so the
        // channel is fully attached before the user sends the next message.
        // This prevents MESSAGE_START from being dropped during channel
        // attachment when send() fires immediately after.
        void ((_ctx$transport$precon = (_ctx$transport = ctx.transport).preconnect) === null || _ctx$transport$precon === void 0 ? void 0 : _ctx$transport$precon.call(_ctx$transport, id));
      }
    } catch (err) {
      if (ctx.getState().fetchIdCounter !== currentFetchId) return;
      ctx.dispatch({
        type: 'TURN_ERROR',
        error: toChatError(err)
      });
    }
  }
  function loadMore() {
    const state = ctx.getState();
    if (!state.historyHasMore || state.historyOffset === undefined || state.isLoadingMore || state.status === 'LOADING' || state.conversationId === null) {
      return;
    }
    ctx.dispatch({
      type: 'HISTORY_LOADING_MORE',
      loading: true
    });
    const currentConversationId = state.conversationId;
    conversationHistory.getConversation({
      conversationId: currentConversationId,
      sortDirection: 'DESC',
      offset: state.historyOffset
    }).then(response => {
      if (ctx.getState().conversationId !== currentConversationId) return;
      const pageUnwrapped = unwrapConversationRecords(response.results);
      ctx.dispatch({
        type: 'HISTORY_PAGE_LOADED',
        messages: pageUnwrapped.messages,
        hasMore: response.hasMore,
        offset: response.offset,
        turnIdMap: pageUnwrapped.turnIdMap,
        feedbackByTurnId: extractFeedbackByTurnId(response.feedbackByKey),
        feedbackByCitationKey: extractFeedbackByCitationKey(response.feedbackByKey)
      });
    }).catch(err => {
      var _callbacks$onError;
      if (ctx.getState().conversationId !== currentConversationId) return;
      ctx.dispatch({
        type: 'HISTORY_LOADING_MORE',
        loading: false
      });
      const chatError = toChatError(err, 'Failed to load older messages');
      ctx.dispatch({
        type: 'TURN_ERROR_SOFT',
        error: chatError
      });
      (_callbacks$onError = callbacks.onError) === null || _callbacks$onError === void 0 || _callbacks$onError.call(callbacks, chatError);
    });
  }
  return {
    loadConversation,
    loadMore
  };
}